import { useEffect, useRef, useState } from 'react';
import {
  updateProjectOutcome,
  ApiError,
  type UpdateProjectOutcomeInput,
} from '../../lib/apiClient';
import { ProjectOutcomeStatus, type ProjectOutcome } from '../../graphql/types';
import '@tylertech/forge/dialog';
import '@tylertech/forge/button';
import '@tylertech/forge/banner';
import './EditCatalogDialog.css';

export interface EditOutcomeDialogProps {
  /** Outcome being edited; `null` keeps the dialog closed. */
  outcome: ProjectOutcome | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export function EditOutcomeDialog({
  outcome,
  onSuccess,
  onCancel,
}: EditOutcomeDialogProps): JSX.Element {
  const dialogRef = useRef<HTMLElement & { open: boolean }>(null);
  const [form, setForm] = useState<{
    name: string;
    description: string;
    displayOrder: string;
    status: ProjectOutcomeStatus;
  }>({ name: '', description: '', displayOrder: '', status: ProjectOutcomeStatus.Active });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = outcome !== null;

  const DialogElement = 'forge-dialog' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.open = open;
    }
    if (outcome) {
      setForm({
        name: outcome.name,
        description: outcome.description ?? '',
        displayOrder: String(outcome.displayOrder),
        status: outcome.status,
      });
      setError(null);
    }
  }, [outcome, open]);

  const handleClose = (): void => {
    if (!isSubmitting) {
      onCancel();
    }
  };

  const canSubmit = form.name.trim() !== '' && !isSubmitting;

  const handleSubmit = async (): Promise<void> => {
    if (!outcome || !canSubmit) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const input: UpdateProjectOutcomeInput = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        displayOrder: Number(form.displayOrder) || outcome.displayOrder,
        status: form.status,
      };
      await updateProjectOutcome(outcome.entityId, input);
      onSuccess();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update outcome');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogElement
      ref={dialogRef}
      onForge-dialog-close={handleClose}
      className="edit-catalog-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-outcome-title"
    >
      <div className="dialog-content">
        <h2 id="edit-outcome-title" className="forge-typography--headline5 edit-catalog-title">
          Edit Outcome
        </h2>

        {error && (
          <BannerElement
            theme="error"
            dismissed={false}
            role="alert"
            className="edit-catalog-error"
          >
            <span>{error}</span>
          </BannerElement>
        )}

        <div className="edit-catalog-body">
          <div className="edit-catalog-field">
            <label className="forge-typography--caption edit-catalog-label" htmlFor="edit-oc-name">
              Name *
            </label>
            <input
              id="edit-oc-name"
              className="search-input"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Outcome name"
              aria-label="Outcome name"
              aria-required="true"
            />
          </div>

          <div className="edit-catalog-field">
            <label className="forge-typography--caption edit-catalog-label" htmlFor="edit-oc-order">
              Display Order
            </label>
            <input
              id="edit-oc-order"
              type="number"
              className="search-input"
              value={form.displayOrder}
              onChange={(e) => setForm((p) => ({ ...p, displayOrder: e.target.value }))}
              aria-label="Outcome display order"
            />
          </div>

          <div className="edit-catalog-field">
            <label
              className="forge-typography--caption edit-catalog-label"
              htmlFor="edit-oc-status"
            >
              Status
            </label>
            <select
              id="edit-oc-status"
              className="filter-select"
              value={form.status}
              onChange={(e) =>
                setForm((p) => ({ ...p, status: e.target.value as ProjectOutcomeStatus }))
              }
              aria-label="Outcome status"
            >
              <option value={ProjectOutcomeStatus.Active}>Active</option>
              <option value={ProjectOutcomeStatus.Archived}>Archived</option>
            </select>
          </div>

          <div className="edit-catalog-field">
            <label className="forge-typography--caption edit-catalog-label" htmlFor="edit-oc-desc">
              Description
            </label>
            <input
              id="edit-oc-desc"
              className="search-input"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Optional description"
              aria-label="Outcome description"
            />
          </div>
        </div>

        <div className="dialog-actions">
          <ForgeButton type="button" variant="text" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </ForgeButton>
          <ForgeButton
            type="button"
            variant="raised"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
          >
            {isSubmitting ? 'Saving…' : 'Save'}
          </ForgeButton>
        </div>
      </div>
    </DialogElement>
  );
}
